import { FormikProvider, useFormik } from 'formik'

import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectFederalEstimateByTaxQuarter } from '@/features/taxes/quarterly/userTaxEstimates.selector'
import {
  CALCULATE_SAFE_HARBOR_STEP,
  calculateSafeHarbor,
  safeHarborMultiplier,
  useCalculateSafeHarborSteps,
} from './helpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  SafeHarborType,
  UPDATE_USER_TAX_ESTIMATES_KEY,
  updateUserTaxEstimates,
} from '@/features/taxes/quarterly/userTaxEstimates.slice'
import {
  dollarsToCents,
  dollarsToCurrency,
  formatCurrency,
} from '@/utils/currencyHelpers'
import { Alert, Card, CardColors, Form, Text } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'
import { getFetchError } from '@/reducers/fetch'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from '@/features/taxes/taxes-profile/form-flow/TaxProfileAccordions'
import { CalculateSafeHarborFooter } from './CalculateSafeHarborFooter'
import { ImageOffset } from '@/components/ImageOffset'
import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'

// TODO: Migrate to V2
import { makeNumberSchema, getFieldName } from '@/componentsV1/BaseComponents'

export const TotalTaxesAndIncome = ({
  details,
}: {
  details?: QuarterlyTaxEstimateDetail | null
}) => {
  const dispatch = useAppDispatch()

  const { goToPreviousStep, goToNextStep, hasPriorQuarterStep } =
    useCalculateSafeHarborSteps(
      CALCULATE_SAFE_HARBOR_STEP.totalTaxesAndIncome,
      details?.taxQuarter
    )

  const federalTaxEstimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${details?.taxYear}-${details?.taxQuarter}`
  )

  const fp = useReselector(getFinancialProfile)

  const error = useReselector(getFetchError, UPDATE_USER_TAX_ESTIMATES_KEY)

  // declared early so it can be used in onSubmit
  let safeHarborAmount: number | null = null

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      adjustedGrossIncome: null as number | null,
      totalFederalTaxes: null as number | null,
    },
    onSubmit: async () => {
      if (!federalTaxEstimate) {
        return
      }

      const res = await dispatch(
        updateUserTaxEstimates(federalTaxEstimate?.id, {
          estimateInCents: dollarsToCents(safeHarborAmount),
          safeHarborType: SafeHarborType.calculated,
        })
      )

      if (res) {
        goToNextStep()
      }
    },
  })

  const { values, isSubmitting, isValid, submitForm } = formik

  safeHarborAmount = calculateSafeHarbor({
    adjustedGrossIncomeInDollars: values.adjustedGrossIncome,
    totalFederalTaxesInDollars: values.totalFederalTaxes,
    filingStatus: fp?.filingStatus,
  })

  const multiplier = safeHarborMultiplier({
    adjustedGrossIncomeInDollars: values.adjustedGrossIncome,
    filingStatus: fp?.filingStatus,
  })

  const previousYear = Number(details?.taxYear) - 1

  const federalTaxesSchema = makeNumberSchema({
    allowedDecimals: 2,
    required: false,
  }).test(
    'federalTaxesValidation',
    'Total federal taxes cannot exceed your adjusted gross income. Please double-check the numbers you’ve entered.',
    (federalTaxes) => {
      return (
        !federalTaxes ||
        !values?.adjustedGrossIncome ||
        dollarsToCurrency(values.adjustedGrossIncome).value >=
          dollarsToCurrency(federalTaxes).value
      )
    }
  )

  const faqCopy = useTaxProfileAccordionCopy()

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-6 max-w-2xl mx-auto mt-10">
        <ImageOffset
          imageLink="https://heard-images.s3.amazonaws.com/assets/1040_adjusted_gross_income.svg"
          width={523}
          height={71}
          backgroundPosition="0 -210px"
          linkText="1040 Page 1, Adjusted Gross Income"
        />

        <ImageOffset
          imageLink="https://heard-images.s3.amazonaws.com/assets/1040_total_taxes.svg"
          width={523}
          height={136}
          linkText="1040 Page 2, Total Taxes"
        />

        <div className="mx-auto w-full max-w-5xl text-center">
          <Text variant={TypographyStyles.DISPLAY_MD}>
            How much did you pay in <b>total federal taxes</b> for{' '}
            {previousYear}?
          </Text>
        </div>

        {error && (
          <Alert
            className="max-w-5xl w-full mx-auto"
            variant="error"
            description={error.message}
          />
        )}

        <div className="max-w-5xl w-full mx-auto">
          <Form.Input
            name={getFieldName<typeof values>('adjustedGrossIncome')}
            required
            componentType="currency"
            schema={makeNumberSchema({
              allowedDecimals: 2,
              greaterThanZero: true,
            })}
            label="Adjusted gross income"
            description="You can find this amount on page 1, line 11 of your 1040 Personal Tax return."
            placeholder="$"
          />
        </div>

        <div className="max-w-5xl w-full mx-auto">
          <Form.Input
            name={getFieldName<typeof values>('totalFederalTaxes')}
            componentType="currency"
            schema={federalTaxesSchema}
            label={`Total ${previousYear} federal taxes`}
            description="You can find this amount on page 2, line 24 of your 1040 Individual Tax return."
            placeholder="$"
          />
        </div>

        {values.totalFederalTaxes && Number(values.totalFederalTaxes) === 0 && (
          <Alert
            className="max-w-5xl w-full mx-auto"
            title="Are you sure?"
            variant="warning"
            description={`It looks like you entered $0 for your ${previousYear} federal taxes, which is uncommon. Check your return to make sure you have the correct number.`}
          />
        )}

        <Card className="max-w-4xl w-full mx-auto" color={CardColors.LEVEL_1}>
          <div className="flex flex-col gap-1">
            <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
              federal
            </Text>
            <Text variant={TypographyStyles.DISPLAY_SM}>
              {safeHarborAmount !== null
                ? formatCurrency(safeHarborAmount, { hideDecimalsIfZero: true })
                : '$--'}
            </Text>
            <Text variant={TypographyStyles.BODY_MD} color={TextColors.MUTED}>
              Safe harbor estimate, per quarter
            </Text>
            {safeHarborAmount !== null && (
              <Text
                variant={TypographyStyles.BODY_SM}
                color={TextColors.MUTED}
                className="mt-2"
              >
                {Math.round(multiplier * 100)}% of your {previousYear} federal
                taxes (
                {formatCurrency(values.totalFederalTaxes, {
                  hideDecimalsIfZero: true,
                })}
                ), divided by 4.
              </Text>
            )}
          </div>
        </Card>

        {safeHarborAmount !== null && (
          <Text color={TextColors.MOSS} className="max-w-5xl w-full mx-auto">
            This amount is inclusive of other federal taxes withheld. Paying it
            each quarter will reduce your risk of a federal underpayment penalty
            at the end of the year.
            {hasPriorQuarterStep &&
              " Next we'll ask about the quarters that have already passed, so we can tell you what to pay now."}
          </Text>
        )}

        <TaxProfileAccordions
          faqs={[
            faqCopy.whyNeedKnowAmountTaxes,
            faqCopy.whatIsSafeHarbor,
            faqCopy.willPaySafeHarborNoTaxes,
            faqCopy.whyEstimateHigh,
            faqCopy.taxesWithheldW2,
          ]}
        />

        <CalculateSafeHarborFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
          nextText={hasPriorQuarterStep ? undefined : 'Save'}
        />
      </div>
    </FormikProvider>
  )
}
